// E9.9: agent-host EOF 帧 quota snapshot 解析 + 全局 bus.
// RoomMembers / AdapterConfigSection 等组件订阅 useQuotaSnapshot(adapterId)
// 拿到最新进度条数据.

import { useEffect, useState } from "react";

export interface QuotaSnapshot {
  day_tokens_used: number;
  day_tokens_limit: number | null;
  day_usd_used: number;
  day_usd_limit: number | null;
  month_tokens_used: number;
  month_tokens_limit: number | null;
  total_usd_used: number;
  total_usd_limit: number | null;
}

// adapterId → snapshot. 单进程内 in-memory 缓存, 每个 EOF 帧覆盖一次.
const cache = new Map<string, QuotaSnapshot>();
const bus = new EventTarget();
const EVT = "quota-update";

export function updateQuotaSnapshot(adapterId: string, snap: QuotaSnapshot): void {
  cache.set(adapterId, snap);
  bus.dispatchEvent(new CustomEvent(EVT, { detail: adapterId }));
}

export function getQuotaSnapshot(adapterId: string): QuotaSnapshot | null {
  return cache.get(adapterId) ?? null;
}

/** React hook: 订阅某 adapter 的 quota 变化, 返回最新 snapshot 或 null. */
export function useQuotaSnapshot(adapterId: string): QuotaSnapshot | null {
  const [snap, setSnap] = useState<QuotaSnapshot | null>(
    () => cache.get(adapterId) ?? null,
  );
  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent<string>).detail;
      if (id === adapterId) {
        setSnap(cache.get(adapterId) ?? null);
      }
    };
    bus.addEventListener(EVT, handler as EventListener);
    // mount 时同步当前缓存值 (避免 refresh 时 stale)
    setSnap(cache.get(adapterId) ?? null);
    return () => bus.removeEventListener(EVT, handler as EventListener);
  }, [adapterId]);
  return snap;
}

/** 解析 server EOF 帧的 quota 字段 (JSON 任意 unknown 形, 容错). null 返回则不更新. */
export function parseQuotaFromEof(payload: unknown): QuotaSnapshot | null {
  if (!payload || typeof payload !== "object") return null;
  const q = payload as Record<string, unknown>;
  const num = (v: unknown): number => (typeof v === "number" ? v : 0);
  const numOrNull = (v: unknown): number | null =>
    typeof v === "number" ? v : null;
  return {
    day_tokens_used: num(q.day_tokens_used),
    day_tokens_limit: numOrNull(q.day_tokens_limit),
    day_usd_used: num(q.day_usd_used),
    day_usd_limit: numOrNull(q.day_usd_limit),
    month_tokens_used: num(q.month_tokens_used),
    month_tokens_limit: numOrNull(q.month_tokens_limit),
    total_usd_used: num(q.total_usd_used),
    total_usd_limit: numOrNull(q.total_usd_limit),
  };
}
